import { SignedIn, UserButton } from '@clerk/clerk-react';
import type React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BellIcon, HouseCheckIcon } from './Icons';
import { BottomNav, IconButton, Sidebar } from './ezhoaComponents';
import { useAppSelector } from './hooks';
import type { RootState } from './store/index';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { ezhoaUser } = useAppSelector((state: RootState) => state.clerkAuth);
  const notifications = useAppSelector((state: RootState) => state.notifications?.unreadCount || 0);

  // Don't show layout on auth pages
  if (location.pathname === '/sign-in' || location.pathname === '/sign-up') {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] via-[#F5F5F5] to-[#E8F5E8] dark:from-[#0F172A] dark:via-[#1E3A8A] dark:to-[#312E81] transition-colors duration-300">
      {/* Mobile Header */}
      <header className="mobile-header fixed top-0 left-0 right-0 h-16 z-30 lg:hidden">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <HouseCheckIcon size={20} className="text-white" />
            </div>
            <span className="text-xl font-bold text-white">EZHOA</span>
          </div>

          <div className="flex items-center gap-3">
            <IconButton
              icon={<BellIcon />}
              onClick={() => navigate('/notifications')}
              badge={notifications}
              className="text-white hover:bg-white/20"
            />
            <SignedIn>
              <UserButton
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: 'w-8 h-8',
                    userButtonBox: 'text-white',
                  },
                }}
              />
            </SignedIn>
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="fixed top-0 left-64 right-0 h-16 glass-header z-20 hidden lg:block">
        <div className="flex items-center justify-between h-full px-6">
          <div>
            <h1 className="text-xl font-semibold capitalize">
              {location.pathname.slice(1).replace('-', ' ') || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <IconButton
              icon={<BellIcon />}
              onClick={() => navigate('/notifications')}
              badge={notifications}
            />

            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="font-medium">{ezhoaUser?.name}</p>
                <p className="text-sm text-gray-500 capitalize">{ezhoaUser?.role}</p>
              </div>

              <SignedIn>
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      avatarBox: 'w-10 h-10',
                      userButtonPopoverCard: 'shadow-lg',
                      userButtonPopoverFooter: 'hidden',
                    },
                  }}
                />
              </SignedIn>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar - Desktop Only */}
      <div className="hidden lg:block">
        <Sidebar
          isOpen={true}
          userRole={ezhoaUser?.role || 'homeowner'}
          currentPath={location.pathname}
          onNavigate={(path) => navigate(path)}
          onClose={() => {}}
        />
      </div>

      {/* Main Content */}
      <main className="pt-16 pb-16 lg:pb-0 lg:pl-64 min-h-screen">
        <div className="p-4 lg:p-6">{children}</div>
      </main>

      {/* Bottom Navigation (Mobile) */}
      <BottomNav
        userRole={ezhoaUser?.role || 'homeowner'}
        currentPath={location.pathname}
        onNavigate={(path) => {
          navigate(path);
        }}
      />
    </div>
  );
};
